import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import union from 'lodash.union';
// mui components
import { Button, Grid, Popover, SelectChangeEvent, Typography } from '@mui/material';
// components
import SingleInputDateRangePicker from 'components/single-input-date-range-picker';
import SelectController from 'controllers/select-controller';
import Switch from 'components/switch';
// api
import { useSyncSelectedProject } from 'hooks/useSyncSelectedProject';
import { useGetAppliableFiltersQuery } from 'redux-store/api/activity-feed';
// icons
import FilterIcon from 'assets/icons/buttons/filter.svg';
// styles
import { filtersStyles, typo } from './styles';

export interface IFilterForm {
  phasesIds: number | null;
  phaseItemsIds: number | null;
  posterProfileIds: number[] | null;
  fromDate: Date | null;
  toDate: Date | null;
  isPhotoIncluded?: boolean | null;
  isFileIncluded?: boolean | null;
}

interface IAppliableFilters {
  phases: number[];
  phaseItems: number[];
  profileIds: number[];
}

interface IFilterPopoverProps {
  isHideButtonLabel?: boolean;
  withSwitches?: boolean;
  filtersCount: number;
  appliableFiltersOverride?: IAppliableFilters;
  onSubmit: (formValues: IFilterForm) => void;
  onReset: () => void;
}

const FilterPopover: React.FC<IFilterPopoverProps> = ({
  isHideButtonLabel = false,
  withSwitches = false,
  filtersCount,
  appliableFiltersOverride,
  onSubmit,
  onReset,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const { projectId } = useParams();

  const form = useFormContext<IFilterForm>();

  const [phase, isFileIncluded, isPhotoIncluded, selectedFromDate, selectedToDate, profiles] =
    form.watch([
      'phasesIds',
      'isFileIncluded',
      'isPhotoIncluded',
      'fromDate',
      'toDate',
      'posterProfileIds',
    ]);

  const { project: projectData } = useSyncSelectedProject();

  const { data: apiAppliableFilters, isLoading: isAppliableFiltersLoading } =
    useGetAppliableFiltersQuery(
      { projectId: Number(projectId) },
      { skip: !Number(projectId) || !anchorEl || !!appliableFiltersOverride }
    );

  const appliableFilters = appliableFiltersOverride || apiAppliableFilters;

  const allScheduleItems = projectData?.phases
    ? projectData?.phases?.map(({ items }) => items)?.flat()
    : [];

  const appliablePhases =
    appliableFilters?.phaseItems && appliableFilters?.phases
      ? union(
          Array.from(
            new Set(
              appliableFilters?.phaseItems?.map((scheduleItemId) => {
                return allScheduleItems.find(({ id }) => id === scheduleItemId)?.phaseId;
              })
            )
          ),
          appliableFilters.phases
        ).sort((a, b) => b - a)
      : [];

  const phaseOptions =
    projectData?.phases && appliablePhases?.length
      ? projectData.phases
          .filter(({ id: pId }) => appliablePhases.includes(pId))
          .map(({ id, title, color }) => ({
            value: id,
            label: title,
            color,
          }))
      : [];

  const phaseItemsOptions =
    projectData?.phases && phase && appliableFilters?.phaseItems?.length
      ? projectData.phases
          .find(({ id: phaseId }) => phaseId === phase)
          ?.items?.filter(({ id: pId }) => appliableFilters.phaseItems.includes(pId))
          ?.map(({ id, title }) => ({ value: id, label: title }))
      : [];

  const usersOptions =
    projectData?.profiles && appliableFilters?.profileIds?.length
      ? projectData.profiles
          .filter(({ id: uId }) => appliableFilters.profileIds.includes(uId))
          .map(({ firstName, lastName, id, avatar }) => ({
            value: id,
            label: `${firstName} ${lastName}`,
            icon: avatar,
          }))
      : [];

  const onSwitchChange = ({ target: { checked, name } }: React.ChangeEvent<HTMLInputElement>) => {
    form.setValue(name as keyof IFilterForm, checked);
  };

  const onProfilesChange = ({ target: { value } }: SelectChangeEvent<typeof profiles>) => {
    form.setValue('posterProfileIds', typeof value === 'string' ? null : value);
  };

  const onClear = () => {
    onReset();
    handleClose();
  };

  const onApply = (formValues: IFilterForm) => {
    onSubmit(formValues);
    handleClose();
  };

  const handleDateChange = (fromDate: Date | null, toDate: Date | null) => {
    form.setValue('fromDate', fromDate);
    form.setValue('toDate', toDate);
  };

  const handleRenderValue = (selectedProfiles: number[]) => {
    const selectLabel = usersOptions
      .filter(({ value }) => selectedProfiles.includes(value))
      .map(({ label }) => label)
      .join(', ');
    return (
      <Typography sx={filtersStyles.multipleSelectOption}>
        {selectLabel || 'Select User'}
      </Typography>
    );
  };

  const handleOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const open = Boolean(anchorEl);

  return (
    <>
      <Button
        sx={filtersStyles.mainButton}
        startIcon={<FilterIcon />}
        onClick={handleOpen}
        disableRipple
        className={open ? 'active' : ''}
        endIcon={!!filtersCount && <Grid sx={filtersStyles.count}>{filtersCount}</Grid>}
      >
        {isHideButtonLabel ? '' : 'filters'}
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={filtersStyles.popper}
      >
        <form onSubmit={form.handleSubmit(onApply)}>
          <Grid sx={{ display: 'flex', width: '100%', flexDirection: 'column' }}>
            <Grid sx={filtersStyles.inputsWrapper}>
              <SelectController
                name="phasesIds"
                label="Phase"
                placeholder="Select Phase"
                options={phaseOptions}
                isSelect
                paperStyles={{}}
                disabled={isAppliableFiltersLoading || !phaseOptions?.length}
                fullWidth
                withError
                optionStyles={filtersStyles.optionStyles}
              />
              <SelectController
                name="posterProfileIds"
                label="User"
                placeholder="Select User"
                options={usersOptions}
                isSelect
                paperStyles={{}}
                disabled={isAppliableFiltersLoading || !usersOptions?.length}
                fullWidth
                withError
                optionStyles={filtersStyles.optionStyles}
                withIcons={true}
                multiple
                value={profiles ?? []}
                onChange={onProfilesChange}
                renderValue={handleRenderValue}
              />
              <SelectController
                name="phaseItemsIds"
                label="Item"
                placeholder="Select Item"
                options={phaseItemsOptions}
                isSelect
                paperStyles={{}}
                disabled={isAppliableFiltersLoading || !phaseItemsOptions?.length}
                fullWidth
                withError
                optionStyles={filtersStyles.optionStyles}
              />
              <SingleInputDateRangePicker
                handleAccept={handleDateChange}
                selectedFromDate={selectedFromDate}
                selectedToDate={selectedToDate}
              />
            </Grid>
            {withSwitches ? (
              <Grid sx={filtersStyles.switchesWrapper}>
                <Grid sx={filtersStyles.switchContainer}>
                  <Switch
                    name="isPhotoIncluded"
                    onChange={onSwitchChange}
                    checked={!!isPhotoIncluded}
                  />
                  <Typography sx={typo}>Include photos</Typography>
                </Grid>
                <Grid sx={filtersStyles.switchContainer}>
                  <Switch
                    name="isFileIncluded"
                    onChange={onSwitchChange}
                    checked={!!isFileIncluded}
                  />
                  <Typography sx={typo}>Include attachments</Typography>
                </Grid>
              </Grid>
            ) : null}
          </Grid>

          <Grid sx={filtersStyles.btnsContainer}>
            <Button sx={filtersStyles.clearButton} onClick={onClear}>
              Clear
            </Button>
            <Grid sx={filtersStyles.formActions} className="small">
              <Button sx={filtersStyles.formActionButton} type="button" onClick={handleClose}>
                cancel
              </Button>
              <Button sx={filtersStyles.formActionButton} className="filled" type="submit">
                apply
              </Button>
            </Grid>
          </Grid>
        </form>
      </Popover>
    </>
  );
};

export default FilterPopover;
